import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '~/app/helpers/notify';
import useTranslation from '~/app/hooks/useTranslation';
import { useStoreActions, useStoreState } from '~/store';
import { WorkOrderAutoSaveFieldKey, WorkOrderDetail } from '~/workorder/types';

type TextAutoSaveFieldKey = Extract<WorkOrderAutoSaveFieldKey, 'comment' | 'description'>;

export interface UseWorkOrderTextAutoSaveParams {
    workOrderId: WorkOrderDetail['id'];
    fieldKey: TextAutoSaveFieldKey;
    savedValue: string;
    canEdit?: boolean;
}

export const useWorkOrderTextAutoSave = ({
    workOrderId,
    fieldKey,
    savedValue,
    canEdit = true,
}: UseWorkOrderTextAutoSaveParams) => {
    const { t } = useTranslation('workorder');
    const { t: tAPIResponseMessages } = useTranslation('apiResponseMessages');
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const autoSaveWorkOrderData = useStoreActions(actions => actions.workOrder.autoSaveWorkOrderData);

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const savedRef = useRef(savedValue?.trim() ?? '');
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        savedRef.current = savedValue?.trim() ?? '';
    }, [savedValue]);

    useEffect(() => {
        if (!canEdit) {
            setIsEditing(false);
        }
    }, [canEdit]);

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isEditing]);

    const startEditing = useCallback(() => {
        if (saving || !canEdit) {
            return;
        }
        setDraft(savedRef.current);
        setIsEditing(true);
    }, [canEdit, saving]);

    const handleBlur = useCallback(() => {
        const next = draft.trim();
        setIsEditing(false);

        if (!canEdit) {
            return;
        }

        if (next === savedRef.current) {
            return;
        }

        if (netWorkStatus !== 0) {
            return;
        }

        setSaving(true);
        autoSaveWorkOrderData({ key: fieldKey, workOrderId, value: next })
            .then(response => {
                if (response.success) {
                    savedRef.current = next;
                    notify(tAPIResponseMessages('changesSaved'), 'success');
                }
            })
            .catch(() => {
                notify(t('detail.error.textFieldUpdateFailed'), 'error');
            })
            .finally(() => setSaving(false));
    }, [
        autoSaveWorkOrderData,
        canEdit,
        draft,
        fieldKey,
        netWorkStatus,
        t,
        tAPIResponseMessages,
        workOrderId,
    ]);

    return {
        isEditing,
        draft,
        setDraft,
        startEditing,
        handleBlur,
        saving,
        textareaRef,
    };
};
